import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useStore } from '~/store/store-context';
import { useTemplateVariableDetection } from './hooks/useTemplateVariableDetection';
import { useVariableResolution } from './hooks/useVariableResolution';
import { normalizeVariableLabel } from './TemplateVariables.utils';
const EMPTY_TEMPLATE_VARS = [];
const TemplateVariablesContext = /*#__PURE__*/ createContext(null);
export function TemplateVariablesProvider(param) {
    let { contact, worksAt, agreementName, effectiveDate, initialTemplateVariableValues, textFields, richTextFields, children } = param;
    const businessDefaults = useStore((state)=>{
        var _ref;
        var _state_userInfo_businessSettings, _state_userInfo;
        return (_ref = (_state_userInfo = state.userInfo) === null || _state_userInfo === void 0 ? void 0 : (_state_userInfo_businessSettings = _state_userInfo.businessSettings()) === null || _state_userInfo_businessSettings === void 0 ? void 0 : _state_userInfo_businessSettings.templateVariables) !== null && _ref !== void 0 ? _ref : EMPTY_TEMPLATE_VARS;
    });
    const { detectedVariables, customVariables, systemVariables } = useTemplateVariableDetection(textFields, richTextFields);
    const [templateVariableValues, setTemplateVariableValues] = useState(()=>{
        if (!initialTemplateVariableValues) {
            return {};
        }
        const normalized = {};
        for (const [key, value] of Object.entries(initialTemplateVariableValues)){
            normalized[normalizeVariableLabel(key)] = value;
        }
        return normalized;
    });
    const setTemplateVariableValue = useCallback((label, value)=>{
        setTemplateVariableValues((prev)=>_object_spread_props(_object_spread({}, prev), {
                [normalizeVariableLabel(label)]: value
            }));
    }, []);
    const [currentDate] = useState(()=>new Date());
    const resolutionContext = useMemo(()=>({
            clientCompanyName: contact === null || contact === void 0 ? void 0 : contact.companyName(),
            clientFirstName: contact === null || contact === void 0 ? void 0 : contact.firstName(),
            clientLastName: contact === null || contact === void 0 ? void 0 : contact.lastName(),
            clientEmail: contact === null || contact === void 0 ? void 0 : contact.email(),
            vendorBusinessName: worksAt.name,
            agreementName: agreementName !== null && agreementName !== void 0 ? agreementName : undefined,
            effectiveDate,
            currentDate
        }), [
        contact,
        worksAt.name,
        agreementName,
        effectiveDate,
        currentDate
    ]);
    const resolvedValues = useVariableResolution(detectedVariables, resolutionContext, templateVariableValues, businessDefaults);
    const unresolvedCustomVariables = useMemo(()=>customVariables.filter((label)=>resolvedValues[normalizeVariableLabel(label)] === undefined), [
        customVariables,
        resolvedValues
    ]);
    const [showVariableErrors, setShowVariableErrors] = useState(false);
    const value = useMemo(()=>({
            detectedVariables,
            customVariables,
            systemVariables,
            resolvedValues,
            templateVariableValues,
            setTemplateVariableValue,
            unresolvedCustomVariables,
            hasVariables: detectedVariables.length > 0,
            showVariableErrors,
            setShowVariableErrors
        }), [
        detectedVariables,
        customVariables,
        systemVariables,
        resolvedValues,
        templateVariableValues,
        setTemplateVariableValue,
        unresolvedCustomVariables,
        showVariableErrors
    ]);
    return /*#__PURE__*/ _jsx(TemplateVariablesContext.Provider, {
        value: value,
        children: children
    });
}
const defaultContextValue = {
    detectedVariables: [],
    customVariables: [],
    systemVariables: [],
    resolvedValues: {},
    templateVariableValues: {},
    setTemplateVariableValue: ()=>{},
    unresolvedCustomVariables: [],
    hasVariables: false,
    showVariableErrors: false,
    setShowVariableErrors: ()=>{}
};
export function useTemplateVariables() {
    var _useContext;
    return (_useContext = useContext(TemplateVariablesContext)) !== null && _useContext !== void 0 ? _useContext : defaultContextValue;
}
export function useHasTemplateVariablesProvider() {
    return useContext(TemplateVariablesContext) !== null;
}
export function TemplateVariablesPortalProvider(param) {
    let { value, children } = param;
    return /*#__PURE__*/ _jsx(TemplateVariablesContext.Provider, {
        value: value,
        children: children
    });
}
